// src/services/material.service.ts
import api from './api'
import type { Material } from '@/config/types/material'

export interface MaterialListResponse {
  data: Material[]
  total: number
  per_page: number
  current_page: number
  last_page: number
}

export const materialService = {
  async fetchAll(params?: any): Promise<MaterialListResponse> {
    const res = await api.get<MaterialListResponse>('/materials', { params })
    return res.data
  },

  async fetchById(id: number): Promise<Material> {
    const res = await api.get<Material>(`/materials/${id}`)
    return res.data
  },

  async create(formData: FormData): Promise<Material> {
    const res = await api.post<Material>('/materials', formData, {
      headers: {
        Accept: 'application/json',
      },
      timeout: 60000,
    })
    return res.data
  },

  async update(id: number, formData: FormData): Promise<Material> {
    formData.append('_method', 'PUT');
    const res = await api.post<Material>(`/materials/${id}`, formData, {
      headers: {
        Accept: 'application/json',
      },
      timeout: 60000,
    })
    return res.data
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/materials/${id}`)
  },

  getFileUrl(filePath: string): string {
    const baseUrl = (process.env.VUE_APP_API_URL || 'http://127.0.0.1:8000/api').replace(/\/api\/?$/, '')
    return `${baseUrl}/storage/${filePath}`
  },

  getDownloadUrl(id: number): string {
    const base = (process.env.VUE_APP_API_URL || '/api').replace(/\/$/, '')
    return `${base}/materials/${id}/download`
  },

  async printFile(id: number): Promise<void> {
    const token = localStorage.getItem('token')
    const response = await fetch(this.getDownloadUrl(id), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Failed to fetch file for printing')

    const contentType = response.headers.get('content-type') || ''
    const blob = await response.blob()
    const isPdf = contentType.includes('pdf') || blob.type.includes('pdf')
    const isImage = contentType.startsWith('image/') || blob.type.startsWith('image/')

    if (!isPdf && !isImage) {
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      URL.revokeObjectURL(url)
      throw new Error('This file type cannot be printed directly. The file has been opened in a new tab.')
    }

    const blobUrl = URL.createObjectURL(blob)
    const printWindow = window.open(blobUrl, '_blank')
    if (!printWindow) {
      URL.revokeObjectURL(blobUrl)
      throw new Error('Popup blocked. Please allow popups to print this file.')
    }

    const triggerPrint = () => {
      try {
        printWindow.focus()
        printWindow.print()
      } catch {
        // Browser may block print until the PDF viewer is ready
      }
    }

    printWindow.onload = () => setTimeout(triggerPrint, 1000)
    setTimeout(triggerPrint, 3000)

    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
  },
}
